import {
  createContext,
  useContext,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { db } from "../db/dexie";
// import { applySchema, initializeDefaults } from "../db/schema";
// import { createRepo } from "../db/repoFactory";
import { ensureSchemaUpToDate } from "../db/schema/index";

/**
 * Build all repositories (app-level + feature-level) here.
 * If you add a new feature table, add a new repo below.
 */

// function buildRepos(db) {
//   // App-level (repos built here are available widely through dexie provider)
//   // const appConfig = createRepo(db, "app_config", { keyProp: "key" });
//   const profiles = createRepo(db, "profiles");

//   // // Features (add as you add schema versions)
//   // const todos = db.tables.find((t) => t.name === "todos")
//   //   ? createRepo(db, "todos")
//   //   : null;

//   // const travel = db.tables.find((t) => t.name === "travel_itineraries")
//   //   ? createRepo(db, "travel_itineraries")
//   //   : null;

//   // You can add domain helpers on top:
//   // Add additional specific helper functions for appConfig to the generic set already created

//   // const appConfigRepo = {
//   //   ...appConfig,
//   //   async getValue(key, fallback = null) {
//   //     const row = await app_config.get(key);
//   //     return row ? row.value : fallback;
//   //   },
//   //   async setValue(key, value) {
//   //     return app_config.put({ key, value, modified: Date.now() });
//   //   },
//   // };

//   // Add additional specific helper functions for todo to the generic set already created
//   // const todoRepo = todos && {
//   //   ...todos,
//   //   async listActive() {
//   //     return todos.list((t) => !t.deleted);
//   //   },
//   //   async toggleDone(id) {
//   //     const item = await todos.get(id);
//   //     if (!item) return;
//   //     return todos.update(id, { done: !item.done, modified: Date.now() });
//   //   },
//   //   async softDelete(id) {
//   //     return todos.update(id, { deleted: true, modified: Date.now() });
//   //   },
//   // };

//   // return db instance + any global repos
//   return {
//     db,
//     profiles,
//   };
//   // ,
//   //   // appConfig: appConfigRepo,
//   //   profiles,
//   //   settings,
//   //   // todos: todoRepo,
//   //   // travel,
//   // };
// }

const DexieContext = createContext(null);

// Optional helper if you want to call it elsewhere too
// export async function initDb() {
//   return ensureSchemaUpToDate(db); // pass the singleton instance
// }

export function DexieProvider({
  children,
  updatePromptLevel = "major",
  promptUser,
}) {
  const [readyDb, setReadyDb] = useState(null);

  // Modal state
  const [pendingUpdates, setPendingUpdates] = useState(null);
  const resolverRef = useRef(null);

  const promptUserInternal = useCallback((updates) => {
    setPendingUpdates(updates);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);
  const onApprove = useCallback(() => {
    resolverRef.current?.(true);
    resolverRef.current = null;
    setPendingUpdates(null);
  }, []);
  const onDecline = useCallback(() => {
    resolverRef.current?.(false);
    resolverRef.current = null;
    setPendingUpdates(null);
  }, []);

  // Single, shareable bootstrap promise — persists across StrictMode remounts
  const bootPromiseRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    if (!bootPromiseRef.current) {
      // Start exactly once; store the promise
      bootPromiseRef.current = (async () => {
        console.debug("[DexieProvider] boot start");

        // IMPORTANT: skip prompting on fresh install is handled inside planAndMigrate
        await ensureSchemaUpToDate(db, {
          updatePromptLevel,
          // Only pass a prompt if provided; migrator won’t call it when no pending or fresh install
          promptUser:
            typeof promptUser === "function" ? promptUser : promptUserInternal,
        });

        // installSettingsWriteGuards?.(db);

        console.debug("[DexieProvider] ensureSchemaUpToDate resolved");
        return db;
      })().catch((err) => {
        console.error("[DexieProvider] boot failed:", err);
        throw err;
      });
    }

    // Always attach a then() on every mount so we set state even after StrictMode remounts
    bootPromiseRef.current
      .then((resolvedDb) => {
        if (!cancelled) {
          setReadyDb(resolvedDb);
          console.debug("[DexieProvider] readyDb set");
        }
      })
      .catch(() => {
        // error already logged above
      });

    return () => {
      cancelled = true;
      // If a prompt is open on unmount, resolve as declined to avoid dangling Promise
      if (resolverRef.current) {
        resolverRef.current(false);
        resolverRef.current = null;
      }
    };
  }, [updatePromptLevel, promptUser, promptUserInternal]);

  // While not ready, render a light splash AND allow modal to render (if any)
  if (!readyDb) {
    return (
      <>
        {/* <div className="app-boot-splash">Initializing Database…</div> */}
        {pendingUpdates && (
          <div
            class="modal show-block"
            id="modal-backdrop"
            tabindex="-1"
            role="dialog"
            aria-labelledby="db-update-title"
            aria-hidden="true"
          >
            <div
              class="modal-dialog modal-dialog-centered modal-dialog-scrollable"
              role="document"
            >
              <div class="modal-content">
                <div class="modal-header justify-content-between">
                  <h5 class="modal-title" id="db-update-title">
                    Database Update Available
                  </h5>
                  {/* <button
                    type="button"
                    class="close"
                    data-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">&times;</span>
                  </button> */}
                </div>
                <div class="modal-body">
                  <p>The following database changes are needed:</p>
                  <ul>
                    {pendingUpdates.map((u) => (
                      <li
                        key={`${u.store}-${u.from}-${u.to}`}
                        class="list-unstyled"
                      >
                        <strong>{u.store}</strong>: {u.from} → {u.to} (
                        {u.bumpType})
                        {u.warning && <div className="warn">⚠ {u.warning}</div>}
                        {u.changeDescription && (
                          <ul>
                            <li>{u.changeDescription}</li>
                          </ul>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                <div class="modal-footer">
                  <button
                    type="button"
                    class="btn btn-secondary"
                    onClick={onDecline}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="btn btn-primary"
                    data-dismiss="modal"
                    onClick={onApprove}
                    autoFocus
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <DexieContext.Provider value={{ db: readyDb }}>
      {children}
      {pendingUpdates && (
        <div className="modal-backdrop" role="presentation">
          <div
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="db-update-title"
          >
            <h3 id="db-update-title">Database Update Available</h3>
            <p>The following stores will be updated:</p>
            <ul>
              {pendingUpdates.map((u) => (
                <li key={`${u.store}-${u.from}-${u.to}`}>
                  <strong>{u.store}</strong>: {u.from} → {u.to} ({u.bumpType})
                  {u.warning && <div className="warn">⚠ {u.warning}</div>}
                </li>
              ))}
            </ul>
            <div className="actions">
              <button onClick={onApprove} autoFocus>
                Update
              </button>
              <button onClick={onDecline}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </DexieContext.Provider>
  );
}

// top return
/*

          <div className="modal-backdrop" role="presentation">
            <div
              className="modal show-block"
              role="dialog"
              aria-modal="true"
              aria-labelledby="db-update-title"
            >
              <h3 id="db-update-title">Database Update Available</h3>
              <p>The following stores will be updated:</p>
              <ul>
                {pendingUpdates.map((u) => (
                  <li key={`${u.store}-${u.from}-${u.to}`}>
                    <strong>{u.store}</strong>: {u.from} → {u.to} ({u.bumpType})
                    {u.warning && <div className="warn">⚠ {u.warning}</div>}
                  </li>
                ))}
              </ul>
              <div className="actions">
                <button onClick={onApprove} autoFocus>
                  Update
                </button>
                <button onClick={onDecline}>Cancel</button>
              </div>
            </div>
          </div>


<!-- Modal -->
*/
// previous
// export function DexieProvider({ children, updatePromptLevel = "minor" }) {
//   const [readyDb, setReadyDb] = useState(null);
//   const initRan = useRef(false);
//   const inFlight = useRef(Promise.resolve());

//   const [pendingUpdates, setPendingUpdates] = useState(null);
//   const resolverRef = useRef(null); // 👈 use ref, not state, for the Promise resolver

//   const promptUser = useCallback((updates) => {
//     setPendingUpdates(updates);
//     return new Promise((resolve) => {
//       resolverRef.current = resolve; // store resolver for later
//     });
//   }, []);

//   const onApprove = useCallback(() => {
//     resolverRef.current?.(true);
//     resolverRef.current = null;
//     setPendingUpdates(null);
//   }, []);

//   const onDecline = useCallback(() => {
//     resolverRef.current?.(false);
//     resolverRef.current = null;
//     setPendingUpdates(null);
//   }, []);

//   useEffect(() => {
//     if (initRan.current) return;
//     initRan.current = true;

//     let cancelled = false;

//     inFlight.current = (async () => {
//       console.debug("[DexieProvider] init start");
//       await ensureSchemaUpToDate(db, {
//         updatePromptLevel, // "major" | "minor" | "patch"
//         promptUser, // async (summary) => boolean
//       });
//       console.debug("[DexieProvider] ensureSchemaUpToDate() resolved"); // ★
//       // for debug only
//       // installSettingsWriteGuards?.(db);

//       if (!cancelled) {
//         setReadyDb(db);
//         console.debug("[DexieProvider] readyDb set");
//       }
//     })().catch((err) => {
//       console.error("[DexieProvider] init failed:", err);
//     });

//     return () => {
//       cancelled = true;
//       // If a prompt is open and we unmount, resolve as declined to avoid dangling Promises
//       if (resolverRef.current) {
//         resolverRef.current(false);
//         resolverRef.current = null;
//       }
//     };
//   }, [updatePromptLevel, promptUser]);

//   const isPrompting = !!pendingUpdates;
//   if (!readyDb) {
//     return (
//       <>
//         {/* optional splash while initializing */}
//         <div className="app-boot-splash">Initializing database…</div>

//         {/* allow the update modal to render and resolve the prompt */}
//         {isPrompting && (
//           <div className="modal-backdrop" role="presentation">
//             <div
//               className="modal"
//               role="dialog"
//               aria-modal="true"
//               aria-labelledby="db-update-title"
//             >
//               <h3 id="db-update-title">Database Update Available</h3>
//               <p>The following stores will be updated:</p>
//               <ul>
//                 {pendingUpdates.map((u) => (
//                   <li key={`${u.store}-${u.from}-${u.to}`}>
//                     <strong>{u.store}</strong>: {u.from} → {u.to} ({u.bumpType})
//                     {u.warning && <div className="warn">⚠ {u.warning}</div>}
//                   </li>
//                 ))}
//               </ul>
//               <div className="actions">
//                 <button onClick={onApprove} autoFocus>
//                   Update
//                 </button>
//                 <button onClick={onDecline}>Cancel</button>
//               </div>
//             </div>
//           </div>
//         )}
//       </>
//     );
//   }

//   // if (!readyDb) return null; // or render a lightweight loader

//   // return (
//   //   <DexieContext.Provider value={{ db: readyDb }}>
//   //     {children}

//   //     {pendingUpdates && (
//   //       <div className="modal-backdrop" role="presentation">
//   //         <div
//   //           className="modal"
//   //           role="dialog"
//   //           aria-modal="true"
//   //           aria-labelledby="db-update-title"
//   //         >
//   //           <h3 id="db-update-title">Database Update Available</h3>
//   //           <p>The following stores will be updated:</p>
//   //           <ul>
//   //             {pendingUpdates.map((u) => (
//   //               <li key={`${u.store}-${u.from}-${u.to}`}>
//   //                 <strong>{u.store}</strong>: {u.from} → {u.to} ({u.bumpType})
//   //                 {u.warning && <div className="warn">⚠ {u.warning}</div>}
//   //               </li>
//   //             ))}
//   //           </ul>
//   //           <div className="actions">
//   //             <button onClick={onApprove} autoFocus>
//   //               Update
//   //             </button>
//   //             <button onClick={onDecline}>Cancel</button>
//   //           </div>
//   //         </div>
//   //       </div>
//   //     )}
//   //   </DexieContext.Provider>
//   // );
// }

// /**
//  * Initializes the database and runs any pending migrations.
//  * Safe to call during app bootstrap.
//  *
//  * @returns {Promise<import('dexie').Dexie>}
//  */
// export async function initDb() {
//   const db = await ensureSchemaUpToDate("pwa-app");
//   return db;
// }

// previous
// export function DexieProvider({ children }) {
//   // Apply schema exactly once at app start
//   // applySchema(db);
//   // initializeDefaults(db); // will run only on first creation

//   // const repos = useMemo(() => buildRepos(db), []);
//   const db = useMemo(() => initDb(), []);

//   return (
//     // <DexieContext.Provider  value={repos}>{children}</DexieContext.Provider>
//     <DexieContext.Provider value={{ db }}>{children}</DexieContext.Provider>
//   );
// }

export function useDexie() {
  const ctx = useContext(DexieContext);
  if (!ctx) throw new Error("useDexie must be used within <DexieProvider>");
  return ctx;
}

/** Convenience: pull one repo by name */
export function useRepo(name) {
  const ctx = useDexie();
  const repo = ctx?.[name];
  if (!repo) throw new Error(`Repo '${name}' not found`);
  return repo;
}

export function useDb() {
  return useDexie().db;
}

// for debug only
// temp-guards.js (or inline in DexieProvider after open)
export function installSettingsWriteGuards(db) {
  const t = db.table("settings");
  const REQUIRED = [
    "id",
    "parents",
    "label",
    "type",
    "options",
    "value",
    "description",
    "category",
    "categories",
    "image",
    "order",
    "created",
    "modified",
    "deleted",
  ];

  // Creating: block/scream if required keys are missing
  t.hook("creating", function (primKey, obj) {
    const missing = REQUIRED.filter((k) => !(k in obj));
    if (missing.length) {
      const err = new Error(
        `[settings] creating with missing keys: ${missing.join(", ")}`
      );
      console.error(err, obj);
      throw err; // crash here to get a stacktrace pointing to the writer
    }
  });

  // Updating: Dexie passes a "mods" object of changed fields; reconstruct next object
  t.hook("updating", function (mods, primKey, obj) {
    const next = { ...obj, ...mods };
    const missing = REQUIRED.filter((k) => !(k in next));
    if (missing.length) {
      const err = new Error(
        `[settings] updating to incomplete object: ${missing.join(", ")}`
      );
      console.error(err, { mods, next });
      throw err; // crash here to get a stacktrace pointing to the writer
    }
  });
}
